// Neo's LIFE-SAFETY alarm-change card (ADR 0210, ADR 0455) — rendered in the chat, one
// per request.
//
// Neo never changes a life-safety alarm itself. This card is the consent step:
// it shows exactly what the change does (the server-built `display`) and why it
// asks, and its button posts `POST /v1/neo/alarm-changes/confirm` with the
// owner's own session. The server re-checks the alarm has not changed since the
// card was made (409 otherwise), applies it, and writes the change into the
// conversation. No text parsing decides anything — the click does.
//
// The pure logic lives in lib/neoAlarmChange.ts, which is unit-tested; this
// file holds state + fetch.

import { useState } from 'react';

import { api } from '../api';
import {
  changeConfirmErrorMessage,
  changeRequestButton,
  changeRequestDoneLabel,
  changeRequestHeading,
  deliveryPhrase,
  type AlarmChangeConfirmResponse,
  type NeoAlarmChangeRequest,
} from '../lib/neoAlarmChange';
import { ALARMS_UPDATED_EVENT } from './NeoAlarmProposalCard';

type CardState =
  | { kind: 'idle' }
  | { kind: 'applying' }
  | { kind: 'done'; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'kept' };

export function NeoAlarmChangeCard({
  request,
  conversationId,
}: {
  request: NeoAlarmChangeRequest;
  /** The chat's conversation, read at click time, so the note lands in it. */
  conversationId: () => string | null;
}) {
  const [state, setState] = useState<CardState>({ kind: 'idle' });
  const { display } = request;

  async function confirm(): Promise<void> {
    if (state.kind === 'applying') return;
    setState({ kind: 'applying' });
    const cid = conversationId();
    try {
      const res = await api<AlarmChangeConfirmResponse>('/v1/neo/alarm-changes/confirm', {
        method: 'POST',
        body: {
          boatId: request.boatId,
          alarmId: request.alarmId,
          action: request.action,
          ...(request.patch !== undefined ? { patch: request.patch } : {}),
          expectedUpdatedAt: request.expectedUpdatedAt,
          ...(cid !== null ? { conversationId: cid } : {}),
        },
        // The card's own id, not a fresh key per click — a second press
        // replays the first answer instead of applying twice.
        idempotencyKey: request.clientId,
      });
      if (res.outcome === 'no_change') {
        setState({ kind: 'done', message: 'Nothing to change — it was already set that way.' });
        return;
      }
      const tail = deliveryPhrase(res.outcome, res.delivery ?? '');
      setState({ kind: 'done', message: tail.length > 0 ? `Done — ${tail}.` : 'Done.' });
      window.dispatchEvent(new CustomEvent(ALARMS_UPDATED_EVENT));
    } catch (err) {
      setState({ kind: 'error', message: changeConfirmErrorMessage(err) });
    }
  }

  if (state.kind === 'kept') {
    return (
      <p className="neo-proposal-dismissed" data-testid="neo-alarm-change-kept">
        Alarm left as it is.
      </p>
    );
  }

  const done = state.kind === 'done';
  const busy = state.kind === 'applying';

  return (
    <article className="neo-proposal neo-alarm-change" data-testid="neo-alarm-change">
      <p className="neo-proposal__eyebrow">
        {done
          ? changeRequestDoneLabel(request.action)
          : 'Life-safety alarm — needs your confirmation'}
      </p>
      <div className="neo-proposal__head">
        <h3 className="neo-proposal__title">
          {done ? display.title : `${changeRequestHeading(request.action)} ${display.title}`}
        </h3>
      </div>
      <ul className="neo-proposal__lines" data-testid="neo-alarm-change-lines">
        {display.lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {done ? (
        <p className="neo-proposal__result" role="status" data-testid="neo-alarm-change-done">
          {state.message}
        </p>
      ) : (
        <>
          <p className="neo-alarm-change__why">
            {request.lifeSafety} Nothing has changed until you confirm.
          </p>
          {state.kind === 'error' ? (
            <p className="neo-proposal__error" role="alert" data-testid="neo-alarm-change-error">
              {state.message}
            </p>
          ) : null}
          <div className="neo-proposal__actions">
            <button
              type="button"
              className="neo-proposal__create"
              onClick={() => void confirm()}
              disabled={busy}
              data-testid="neo-alarm-change-confirm"
            >
              {busy ? 'Applying…' : changeRequestButton(request.action)}
            </button>
            <button
              type="button"
              className="neo-inline-button"
              onClick={() => setState({ kind: 'kept' })}
              disabled={busy}
              data-testid="neo-alarm-change-keep"
            >
              Keep it
            </button>
          </div>
        </>
      )}
    </article>
  );
}
